import { useRef, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowRight, Check } from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';
import { getFeature, getRelatedFeatures } from './featureData';

gsap.registerPlugin(ScrollTrigger);

function HeroPattern({ color }) {
  const dots = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 48; i++) {
      arr.push({
        x: 20 + (i % 8) * 60,
        y: 20 + Math.floor(i / 8) * 60,
        r: Math.random() * 3 + 1.5,
        opacity: Math.random() * 0.4 + 0.1,
        delay: Math.random() * 2,
      });
    }
    return arr;
  }, []);

  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 400" preserveAspectRatio="xMidYMid slice">
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.r} fill={color} opacity={d.opacity} className="dot-particle" style={{ animationDelay: `${d.delay}s` }} />
      ))}
      {/* Geometric accent lines */}
      <line x1="0" y1="200" x2="500" y2="200" stroke={color} strokeWidth="0.5" opacity="0.08" />
      <line x1="250" y1="0" x2="250" y2="400" stroke={color} strokeWidth="0.5" opacity="0.08" />
      <circle cx="250" cy="200" r="120" fill="none" stroke={color} strokeWidth="0.5" opacity="0.06" />
      <circle cx="250" cy="200" r="180" fill="none" stroke={color} strokeWidth="0.5" opacity="0.04" />
    </svg>
  );
}

function BrowserMockup({ screenshot, label, theme }) {
  return (
    <div className="rounded-xl overflow-hidden border border-border" style={{ boxShadow: theme === 'dark' ? '0 40px 80px -20px rgba(0,0,0,0.5)' : '0 40px 80px -20px rgba(0,0,0,0.12)' }}>
      <div className="h-8 bg-card border-b border-border flex items-center px-4 gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="text-[10px] font-mono text-muted-foreground ml-3">app.admitrix.com/{label.toLowerCase()}</span>
      </div>
      <img src={screenshot} alt={`Admitrix ${label} interface`} className="w-full" loading="lazy" />
    </div>
  );
}

export default function FeaturePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const feature = getFeature(slug);

  useSEO(feature ? feature.seo : { title: 'Feature — Admitrix', description: '' });

  // Breadcrumb structured data
  useEffect(() => {
    if (!feature) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://admitrix.in/" },
        { "@type": "ListItem", "position": 2, "name": "Features", "item": "https://admitrix.in/#features" },
        { "@type": "ListItem", "position": 3, "name": feature.title, "item": `https://admitrix.in/features/${feature.slug}` },
      ]
    };
    let script = document.getElementById('breadcrumb-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'breadcrumb-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [feature]);

  useGSAP(() => {
    if (!feature) return;
    const ctx = gsap.context(() => {
      // Hero stagger
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      heroTl
        .from('.fp-badge', { y: 20, opacity: 0, duration: 0.5 })
        .from('.fp-headline', { y: 50, opacity: 0, duration: 0.7 }, '-=0.2')
        .from('.fp-subtitle', { y: 20, opacity: 0, duration: 0.5 }, '-=0.3')
        .from('.fp-ctas', { y: 20, opacity: 0, duration: 0.4 }, '-=0.2')
        .from('.fp-hero-visual', { y: 40, opacity: 0, scale: 0.97, duration: 0.9, ease: 'power2.out' }, '-=0.4');

      // Capabilities
      gsap.from('.fp-cap-card', {
        y: 40, opacity: 0, stagger: 0.07, duration: 0.5, immediateRender: false,
        scrollTrigger: { trigger: '.fp-capabilities', start: 'top 80%' },
      });

      // Steps
      gsap.from('.fp-step', {
        y: 30, opacity: 0, stagger: 0.15, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: '.fp-steps', start: 'top 80%' },
      });

      // Showcase parallax
      if (feature.screenshot) {
        gsap.from('.fp-showcase', {
          y: 60, opacity: 0, scale: 0.96, duration: 0.8, immediateRender: false,
          scrollTrigger: { trigger: '.fp-showcase', start: 'top 85%' },
        });
        gsap.to('.fp-showcase-img', {
          yPercent: -4, ease: 'none',
          scrollTrigger: { trigger: '.fp-showcase', start: 'top bottom', end: 'bottom top', scrub: 1.5 },
        });
      }

      // Metrics
      gsap.from('.fp-metric', {
        y: 20, opacity: 0, stagger: 0.1, duration: 0.5, immediateRender: false,
        scrollTrigger: { trigger: '.fp-metrics', start: 'top 85%' },
      });

      // Related
      gsap.from('.fp-related-card', {
        y: 30, opacity: 0, stagger: 0.1, duration: 0.5, immediateRender: false,
        scrollTrigger: { trigger: '.fp-related', start: 'top 85%' },
      });

      // CTA
      gsap.from('.fp-cta-section', {
        y: 40, opacity: 0, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: '.fp-cta-section', start: 'top 85%' },
      });
    }, containerRef);

    return () => ctx.revert();
  }, { scope: containerRef, dependencies: [slug] });

  if (!feature) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-foreground mb-2">Feature not found</h1>
          <Link to="/" className="text-sm underline" style={{ color: '#E04B2A' }}>Back to home</Link>
        </div>
      </div>
    );
  }

  const related = getRelatedFeatures(feature.relatedFeatures);

  return (
    <div ref={containerRef} className="bg-background text-foreground overflow-x-hidden">
      <PublicNav />

      <main>
        {/* ═══ HERO ═══ */}
        <section className="pt-28 pb-16 px-6 lg:px-12 max-w-7xl mx-auto relative">
          {/* Subtle radial glow */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none" style={{ background: `radial-gradient(circle at center, ${feature.color}08 0%, transparent 70%)` }} />

          <div className={`grid ${feature.screenshot ? 'lg:grid-cols-2' : 'lg:grid-cols-1 max-w-3xl'} gap-12 items-center relative z-10`}>
            <div>
              <div className="fp-badge inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-6">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: feature.color }} />
                Feature &middot; {feature.title}
              </div>
              <h1 className="fp-headline font-display font-extrabold text-[clamp(2.2rem,4.5vw,3.8rem)] tracking-[-0.03em] leading-[1.05] text-foreground whitespace-pre-line">
                {feature.headline}
              </h1>
              <p className="fp-subtitle text-lg text-muted-foreground mt-6 leading-relaxed max-w-lg">
                {feature.subtitle}
              </p>
              <div className="fp-ctas flex items-center gap-3 mt-8">
                <button onClick={() => navigate('/contact')} className="h-12 px-7 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg" style={{ backgroundColor: '#E04B2A' }} data-testid="feature-cta-primary">
                  Request a Demo <ArrowRight size={16} weight="bold" />
                </button>
                <Link to="/contact" className="h-12 px-7 rounded-lg text-sm font-semibold border border-border text-foreground flex items-center gap-2 hover:bg-secondary transition-colors" data-testid="feature-cta-secondary">
                  Contact Sales
                </Link>
              </div>
            </div>

            {feature.screenshot ? (
              <div className="fp-hero-visual">
                <BrowserMockup screenshot={feature.screenshot} label={feature.title} theme={theme} />
              </div>
            ) : (
              <div className="fp-hero-visual hidden lg:block relative h-[360px] rounded-xl overflow-hidden border border-border bg-card">
                <HeroPattern color={feature.color} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${feature.color}15`, color: feature.color }}>
                    {feature.capabilities[0] && (() => { const Icon = feature.capabilities[0].icon; return <Icon size={36} weight="bold" />; })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ═══ CAPABILITIES ═══ */}
        <section className="fp-capabilities py-20 px-6 lg:px-12 max-w-7xl mx-auto">
          <div className="max-w-xl mb-12">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: feature.color }}>CAPABILITIES</p>
            <h2 className="font-display text-3xl font-extrabold text-foreground tracking-tight leading-[1.1]">
              Everything you need.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {feature.capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="fp-cap-card group p-5 rounded-xl border border-border bg-card hover:border-foreground/15 transition-all duration-300" data-testid={`cap-${cap.title.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${feature.color}12`, color: feature.color }}>
                    <Icon size={18} weight="bold" />
                  </div>
                  <h3 className="font-display text-sm font-bold text-foreground mb-1.5">{cap.title}</h3>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">{cap.desc}</p>
                </div>
              );
            })}
          </div>
          {feature.docsLink && (
            <div className="mt-8">
              <Link to={feature.docsLink} className="text-[12px] font-semibold hover:underline" style={{ color: feature.color }}>
                Read the documentation →
              </Link>
            </div>
          )}
        </section>

        {/* ═══ HOW IT WORKS ═══ */}
        <section className="fp-steps py-20 px-6 lg:px-12 border-y border-border">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-xl mx-auto mb-14">
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: feature.color }}>HOW IT WORKS</p>
              <h2 className="font-display text-3xl font-extrabold text-foreground tracking-tight leading-[1.1]">Three steps. That's it.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {feature.steps.map((step, i) => (
                <div key={step.num} className="fp-step relative">
                  {/* Connector line */}
                  {i < feature.steps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[calc(50%+24px)] w-[calc(100%-48px)] h-px bg-border" style={{ left: '60%', width: '80%' }} />
                  )}
                  <div className="relative z-10">
                    <span className="font-mono text-[11px] font-bold block mb-3" style={{ color: feature.color }}>{step.num}</span>
                    <h3 className="font-display text-lg font-bold text-foreground mb-2">{step.title}</h3>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SHOWCASE (only if screenshot) ═══ */}
        {feature.screenshot && (
          <section className="py-20 px-6 lg:px-12 max-w-6xl mx-auto">
            <div className="fp-showcase fp-showcase-img">
              <BrowserMockup screenshot={feature.screenshot} label={feature.title} theme={theme} />
            </div>
          </section>
        )}

        {/* ═══ METRICS ═══ */}
        <section className="fp-metrics py-16 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-3 gap-8">
            {feature.metrics.map((m) => (
              <div key={m.label} className="fp-metric text-center">
                <p className="font-display text-4xl md:text-5xl font-extrabold text-foreground">{m.value}</p>
                <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mt-2">{m.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ RELATED FEATURES ═══ */}
        <section className="fp-related py-20 px-6 lg:px-12 max-w-7xl mx-auto">
          <div className="max-w-xl mb-12">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3 text-muted-foreground">EXPLORE MORE</p>
            <h2 className="font-display text-3xl font-extrabold text-foreground tracking-tight leading-[1.1]">Works even better together.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r) => {
              const Icon = r.capabilities[0]?.icon;
              return (
                <Link key={r.slug} to={`/features/${r.slug}`} className="fp-related-card group p-6 rounded-xl border border-border bg-card hover:border-foreground/15 transition-all duration-300" data-testid={`related-${r.slug}`}>
                  {Icon && (
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${r.color}12`, color: r.color }}>
                      <Icon size={18} weight="bold" />
                    </div>
                  )}
                  <h3 className="font-display text-sm font-bold text-foreground mb-1 group-hover:underline">{r.title}</h3>
                  <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">{r.subtitle}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-[11px] font-semibold transition-colors" style={{ color: r.color }}>
                    Learn more <ArrowRight size={12} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ═══ CTA ═══ */}
        <section className="fp-cta-section py-24 px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? `linear-gradient(135deg, ${feature.color}10, transparent)` : `linear-gradient(135deg, ${feature.color}06, transparent)` }}>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">
              Ready to transform<br />your admissions?
            </h2>
            <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">
              Built for Indian education — coaching classes, schools, and franchises. See how it works for your team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
              <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="feature-cta-final">
                Request a Demo <ArrowRight size={16} weight="bold" />
              </button>
              <Link to="/contact" className="h-12 px-8 rounded-lg text-sm font-semibold border border-border text-foreground flex items-center justify-center gap-2 hover:bg-secondary transition-colors">
                Contact Sales
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />

      <style>{`
        @keyframes float-dot {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .dot-particle {
          animation: float-dot 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
